/**
 * The visit on a phone (390) — `docs/visit-page.md` §19.2, Board 16 A, B, E;
 * lead decisions 14 and 19: the section label above every tab (*⌂ OVERVIEW*),
 * then cards, never tables — **Days · 3** as rows, **Estimate vs actual** as
 * three lines, **Ready for Friday** (not-ready rows first, *All 7*), then
 * further down **Visits on J-3271 · 2** and **For the crew · 9**. The rail's
 * other cards are not on a phone's Overview (Board 16).
 */
import { useState, type ReactNode } from 'react'
import type { VisitRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { EM_DASH } from '../lib/format.js'
import { RECORD_TAB_ICONS, NAV_ICONS } from '../frame/icons.js'
import { BoardCard } from './card.js'
import { COMMON, OVERVIEW, PHONE, RAIL } from './copy.js'
import { InternalChip } from './internal-chip.js'
import { SourceChip } from './crew-parts.js'
import { JobVisitsList } from './rail.js'
import { VisitGlyph } from './glyphs.js'
import {
  DayChipView, DeltaTag, ReadyRowRight, dayHours, readyWords, type OverviewHandlers,
} from './overview.js'
import { dayText, hours1, hoursBare, money, percent, signed, signedMoney } from './format.js'

/** The tab's own name over its body (Board 16: *⌂ OVERVIEW*, *MATERIALS*) — the tab row's glyph in green. */
export function PhoneSectionLabel({ icon, label }: { icon: string | undefined; label: string }) {
  const Icon = icon
    ? (RECORD_TAB_ICONS as Record<string, (props: { size?: number }) => ReactNode>)[icon]
      ?? (NAV_ICONS as Record<string, (props: { size?: number }) => ReactNode>)[icon]
    : undefined
  return (
    <div className="flex items-center gap-1.5 pb-2 text-[12px] font-semibold uppercase leading-[normal] tracking-[.05em] text-text-faint" data-phone-section="">
      {Icon && <span className="inline-flex text-accent"><Icon size={14} /></span>}
      {label}
    </div>
  )
}

export function PhoneOverview({ read, handlers }: { read: VisitRead; handlers: OverviewHandlers }) {
  const { overview } = read
  return (
    <div className="flex flex-col gap-3 leading-[normal] tabular-nums" data-visit-overview="phone">
      {!overview.series && <PhoneDays read={read} handlers={handlers} />}
      <PhoneEstimate read={read} />
      {overview.ready && <PhoneReady read={read} handlers={handlers} />}
      <BoardCard
        phone
        title={PHONE.jobVisits(read.rail.jobVisits.job.reference, String(read.rail.jobVisits.count))}
        action={{ label: RAIL.jobVisits.link, glyph: 'chevronRight', onClick: () => handlers.onOpen(`/jobs/${read.rail.jobVisits.job.id}?tab=visits`) }}
      >
        <JobVisitsList card={read.rail.jobVisits} phone onOpen={handlers.onOpen} />
      </BoardCard>
      {read.status !== 'closed' && read.status !== 'cancelled' && (
        <BoardCard
          phone
          title={PHONE.forCrew(String(overview.forCrew.count))}
          action={handlers.onPreview ? { label: OVERVIEW.forCrewCard.preview, glyph: 'eye', onClick: handlers.onPreview } : null}
        >
          {overview.forCrew.notes.map((note) => (
            <div key={note.id} className="flex flex-col gap-1.5 border-t border-line-faint px-3.5 py-[11px]">
              <div><SourceChip source={note.source} /></div>
              <span className="text-[13px] leading-[1.45]">
                {note.text}
                {note.source === 'visit' && note.by && <span className="text-text-faint">{' '}{COMMON.count(note.by.name)}</span>}
              </span>
            </div>
          ))}
        </BoardCard>
      )}
    </div>
  )
}


function PhoneDays({ read, handlers }: { read: VisitRead; handlers: OverviewHandlers }) {
  const { days } = read.overview
  return (
    <BoardCard
      phone
      title={PHONE.days(String(days.count))}
      action={days.canAddDay && handlers.onAddDay ? { label: PHONE.add, glyph: 'plus', onClick: handlers.onAddDay } : null}
    >
      {days.days.map((tile) => {
        const { big, small } = dayHours(tile)
        const today = tile.day.relative === 'today'
        const sub = tile.day.relative
          ? COMMON.dateRelative(dayText(tile.day.date, 'day'), tile.day.relative === 'today' ? COMMON.today : COMMON.tomorrow)
          : dayText(tile.day.date, 'date')
        const smallLine = tile.hours.kind === 'actual' && tile.over
          ? PHONE.ofPlannedDelta(hoursBare(tile.hours.planned), signed(tile.over.delta))
          : small
        return (
          <button key={tile.day.index} type="button" onClick={() => handlers.onOpenDay?.(tile.day.index)}
            className={cn('flex w-full items-center gap-2.5 border-t border-line-faint px-3.5 py-[11px] text-left', today && 'bg-green-band-bg')}>
            <span className="flex w-[76px] flex-col">
              <b className="text-[13.5px] font-bold">{COMMON.dayN(String(tile.day.index))}</b>
              <span className="text-[11.5px] text-text-muted">{tile.day.relative === 'tomorrow' ? dayText(tile.day.date, 'date') : sub}</span>
            </span>
            <span className="flex flex-1 flex-col">
              <b className="text-[15px] font-bold">{big}</b>
              <span className="text-[11px] text-text-muted">{smallLine}</span>
            </span>
            <DayChipView chip={tile.chip} />
            <span className="inline-flex shrink-0 text-text-faint"><VisitGlyph name="chevronRight" size={14} stroke={2} /></span>
          </button>
        )
      })}
    </BoardCard>
  )
}

function PhoneLine({ label, value, line, tag }: { label: string; value: string; line: string; tag: ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-t border-line px-3.5 py-[9px] text-[13px]">
      <span className="flex-1 text-text-body">{label}</span>
      <b className="font-bold">{value}</b>
      <span className="text-[11.5px] text-text-muted">{line}</span>
      {tag}
    </div>
  )
}

function PhoneEstimate({ read }: { read: VisitRead }) {
  const card = read.overview.estimate
  const { hours } = card
  const internal = card.internal
  /*
   * D23 (§4.2): no plan lines, so no estimate to be against — the line says
   * so and no chip measures against a zero; projected cost and margin are *—*
   * until something costs anything (walkthrough Oct 30, §9).
   */
  const none = card.noEstimate
  return (
    <BoardCard phone title={OVERVIEW.estimate.title} chip={internal ? <InternalChip /> : undefined} internal={Boolean(internal)}>
      <PhoneLine label={OVERVIEW.estimate.hours} value={hours1(hours.soFar)}
        line={none ? OVERVIEW.estimate.noEstimate : PHONE.ofProjected(hoursBare(hours.estimated), hours1(hours.projected))}
        tag={none ? null : <DeltaTag delta={hours.delta}>{COMMON.h(signed(hours.delta))}</DeltaTag>} />
      {internal && (
        <PhoneLine label={OVERVIEW.estimate.cost} value={money(internal.cost.soFar)}
          line={PHONE.projected(internal.cost.projected === null ? EM_DASH : money(internal.cost.projected))}
          tag={none || internal.cost.projected === null ? null
            : <DeltaTag delta={internal.cost.delta}>{signedMoney(internal.cost.delta)}</DeltaTag>} />
      )}
      {internal?.margin && (
        <PhoneLine label={OVERVIEW.estimate.margin}
          value={internal.margin.percent === null ? EM_DASH : percent(internal.margin.percent)}
          line={none ? OVERVIEW.estimate.noEstimate : PHONE.estimated(percent(internal.margin.estimated))}
          tag={none || internal.margin.percent === null ? null
            : <DeltaTag delta={internal.margin.delta}>{COMMON.pts(signed(internal.margin.delta))}</DeltaTag>} />
      )}
    </BoardCard>
  )
}

function PhoneReady({ read, handlers }: { read: VisitRead; handlers: OverviewHandlers }) {
  const card = read.overview.ready!
  const [all, setAll] = useState(false)
  const rows = all ? card.rows : card.rows.slice(0, 3)
  return (
    <BoardCard
      phone
      title={OVERVIEW.ready.title(dayText(card.day.date, 'weekday'))}
      right={<span className="text-[12px] font-bold">{COMMON.of(String(card.readyCount), String(card.total))}</span>}
    >
      {rows.map((row, index) => {
        const { title, line } = readyWords(read, row)
        return (
          <div key={index} className={cn('flex items-start gap-2.5 border-t border-line-faint px-3.5 py-[11px]', row.mark === 'needs' && 'bg-amber-row')}>
            <PhoneMark mark={row.mark} />
            <div className="flex min-w-0 flex-1 flex-col">
              <b className="text-[13.5px] font-bold leading-[1.3]">{title}</b>
              {line && <span className="mt-0.5 text-[12px] text-text-muted">{line}</span>}
              {row.kind === 'material' && row.how === 'to_order' && (
                <button type="button" onClick={() => row.lines[0] && handlers.onOrder?.(row.lines[0].id)}
                  className="mt-2 inline-flex h-9 items-center justify-center gap-1.5 self-start whitespace-nowrap rounded-[9px] border border-accent bg-accent px-3.5 text-[13px] font-semibold text-white">
                  <VisitGlyph name="cart" size={15} stroke={2} />
                  {OVERVIEW.ready.order}
                </button>
              )}
              {!(row.kind === 'material' && row.how === 'to_order') && <ReadyRowRight row={row} handlers={handlers} phone />}
            </div>
          </div>
        )
      })}
      {!all && card.rows.length > rows.length && (
        <div className="border-t border-line-faint px-3.5 py-2.5">
          <button type="button" onClick={() => setAll(true)}
            className="inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-accent">
            <VisitGlyph name="chevronRight" size={13} stroke={2.2} />
            {OVERVIEW.ready.all(String(card.total))}
          </button>
        </div>
      )}
    </BoardCard>
  )
}

function PhoneMark({ mark }: { mark: 'ready' | 'on_the_way' | 'needs' }) {
  if (mark === 'needs') {
    return (
      <span className="box-border inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-warn-line bg-warn-bg text-warn">
        <VisitGlyph name="warn" size={12} stroke={2.4} />
      </span>
    )
  }
  if (mark === 'on_the_way') {
    return (
      <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-info-bg text-info">
        <VisitGlyph name="clock" size={13} stroke={2.2} />
      </span>
    )
  }
  return (
    <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-accent text-white">
      <VisitGlyph name="check" size={12} stroke={3} />
    </span>
  )
}
